import { MuiButton } from "@/components/ui/mui-button";
import { MuiHomeIcon, MuiRefreshIcon } from "@/components/ui/mui-map-icons";
import { MuiTypography } from "@/components/ui/mui-typography";
import { cn } from "@/lib/utils";

// The communities domain's `FullPageError` (`src/domains/communities/shared/
// components/full-page-error/full-page-error.tsx`) — the list's failed first
// load and the details page's 404 and error states. Shared here because the
// donor keeps it at the domain's shared level, and both screens mount it.
//
// It renders **inside** the page chrome, as the source's does. It is not the
// app's chrome-less `ErrorSurface` (the 404/403/500 set), which answers a
// different situation — a route or an app with nowhere to go — rather than a
// page whose data would not load.
//
// Fact by fact, each measured on the running donor's 404 state:
//
//   - a centred white column, `minHeight: 400px`, `p: 4`, its stack 24px apart
//     (`:17-29`);
//   - the heading an `h4` reading the literal **"404 - Not Found"** when
//     `is404`, the `title` otherwise — so the details page's "Community Not
//     Found" never renders (`:30-31`);
//   - the message `body1` in `text.secondary`, centred (`:33`);
//   - a row of buttons 16px apart, only when there is an action (`:36-57`):
//     contained "Refresh Page" with MUI's `Refresh` as its start icon, disabled
//     and loading while `isLoading` (react-ui's start-position loading), and
//     the caller's secondary action with `Home` — outlined beside a retry,
//     contained alone.
//
// **The heading's ink is the text's in both states (#740 D1).** The source
// asks for `color={is404 ? 'error' : 'text.primary'}`, but react-ui's
// `Typography` moves `color` into `sx`, where `'error'` resolves to the whole
// `palette.error` object and emits no colour (`system-props.ts:69`,
// `typography.tsx:22,33`) — measured `rgba(0, 0, 0, 0.87)` on the running
// donor. Kept so by the owner's ruling (#740 D1); red would be a named break.

const DEFAULT_TITLE = "Something went wrong";
const DEFAULT_MESSAGE = "An error occurred while loading the data. Please try refreshing the page.";

interface FullPageErrorProps {
  title?: string;
  message?: string;
  /** "Refresh Page" — a refetch, never a browser reload; the caller's. */
  onRetry?: () => void;
  /** Head it "404 - Not Found" in place of the title. */
  is404?: boolean;
  /** The retry is running: "Refresh Page" disables and shows its spinner. */
  isLoading?: boolean;
  /** The second button — the details page's "Go to Communities". */
  secondaryAction?: { label: string; onClick: () => void };
  className?: string;
}

export function FullPageError({
  title = DEFAULT_TITLE,
  message = DEFAULT_MESSAGE,
  onRetry,
  is404 = false,
  isLoading = false,
  secondaryAction,
  className,
}: FullPageErrorProps) {
  return (
    <div
      data-slot="full-page-error"
      className={cn(
        "flex min-h-[400px] w-full flex-col items-center justify-center bg-white p-8",
        className,
      )}
    >
      <div className="flex flex-col items-center gap-6">
        <MuiTypography variant="h4" color="primary">
          {is404 ? "404 - Not Found" : title}
        </MuiTypography>
        <MuiTypography variant="body1" color="secondary" className="text-center">
          {message}
        </MuiTypography>
        {onRetry || secondaryAction ? (
          <div className="flex flex-row gap-4">
            {onRetry ? (
              <MuiButton
                variant="contained"
                startIcon={<MuiRefreshIcon />}
                onClick={onRetry}
                loading={isLoading}
              >
                Refresh Page
              </MuiButton>
            ) : null}
            {secondaryAction ? (
              <MuiButton
                variant={onRetry ? "outlined" : "contained"}
                startIcon={<MuiHomeIcon />}
                onClick={secondaryAction.onClick}
              >
                {secondaryAction.label}
              </MuiButton>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
